import React, { useMemo } from 'react';
import { Box, List, Text } from '@common-fe/common-fe';

import { LMNClaim } from '@/modules/MyDocuments/myDocuments.types';
import { useGetClaimServicesQuery } from '@/modules/MyDocuments/queries/useGetClaimServices.query';

import AssociateWithClaimsListHeader from '../AssociateWithClaimsList/AssociateWithClaimsListHeader';
import AssociateWithClaimsListItem from '../AssociateWithClaimsList/AssociateWithClaimsListItem';

interface Props {
  testId?: string;
  title: string;
  claims?: LMNClaim[];
  isStillRequiringAttention?: boolean;
  isMobile?: boolean;
}

const ClaimsReviewList: React.FC<Props> = ({ testId, title, claims, isStillRequiringAttention, isMobile }) => {
  const claimsServicesIds = useMemo(() => {
    const servicesIds = claims?.map((claim) => claim?.serviceId)?.filter(Boolean) as string[] || [];
    return [...new Set(servicesIds)];
  }, [claims]);

  const { services, isLoading: isServicesLoading } = useGetClaimServicesQuery({
    serviceIds: claimsServicesIds,
    enabled: !!claimsServicesIds?.length
  });
  
  const claimsWithServices = useMemo(() => claims?.map((claim) => {
    const claimService = services?.find((service) => service?.id === claim?.serviceId);
    return {
      ...claim,
      ...claimService,
    };
  }), [claims, services]);

  const children = useMemo(() => {
    if (isServicesLoading) {
      return <List.Preloader testId="associated-claims-list_preloader" />;
    }

    return claimsWithServices?.map((claim) => (
      <AssociateWithClaimsListItem
        key={claim?.id}
        claimId={claim?.claimId}
        providerName={claim?.providerName}
        categoryType={claim?.categoryType}
        serviceName={claim?.name}
        serviceOn={claim?.serviceOn}
        amount={claim?.requestedAmount}
        isMobile={isMobile}
        {...isStillRequiringAttention ? {
          border: { color: 'warningBorder', size: '2px' }
        } : {
          background: "infoContainer",
          border: { color: 'infoBorder', size: '2px' }
        }}
      />
    ));
  }, [claimsWithServices, isServicesLoading, isStillRequiringAttention, isMobile]);

  return (
    <Box data-testId={testId}>
      <Text weight={700}>{title}</Text>
      <Box margin={{ top: 'spacing8' }}>
        <List>
          {isMobile ? null : <AssociateWithClaimsListHeader />}
          {children}
        </List>
      </Box>
    </Box>
  );
};

export default ClaimsReviewList;
